import { Observable, Subscribable, SubscribableOrValue } from "knockout";
import WCCAnimator from "managers/animator_new";
import { WCCSliderAlignment } from "../enums/alignment";
import WCCSliderPage from "../models/page";
import { IWCCSliderAnimator } from "./animator.interfaces";

export interface WCCSliderBaseAnimatorConfig<T> {
    shift: Observable<number>

    width: SubscribableOrValue<number>
    spaceBetweenPages: SubscribableOrValue<number>
    otherPageElementWidth: SubscribableOrValue<number>    
    pageIdx: SubscribableOrValue<number>
    alignment: SubscribableOrValue<WCCSliderAlignment>

    showOtherPages: SubscribableOrValue<boolean>

    pages: SubscribableOrValue<WCCSliderPage<T>[]>
}

export default abstract class WCCSliderBaseAnimator<T, C> extends WCCAnimator<C> implements IWCCSliderAnimator {
    protected shift: Observable<number>

    protected width: Subscribable<number>
    protected spaceBetweenPages: Subscribable<number>
    protected otherPageElementWidth: Subscribable<number>    
    protected nextShift: Subscribable<number>
    protected pageIdx: Subscribable<number>

    protected alignment: Subscribable<WCCSliderAlignment>   

    protected areOtherPagesShown: Subscribable<boolean>

    protected pages: Subscribable<WCCSliderPage<T>[]>

    constructor(config: WCCSliderBaseAnimatorConfig<T>) {
        super();

        this.shift = config.shift;

        this.width = ko.flattenComputed(config.width);
        this.spaceBetweenPages = ko.flattenComputed(config.spaceBetweenPages);
        this.otherPageElementWidth = ko.flattenComputed(config.otherPageElementWidth);

        this.alignment = ko.flattenComputed(config.alignment);

        this.nextShift = ko.pureComputed(() => this.getShift()).extend({ rateLimit: 50, notifyIfChanged: true });

        this.pages = ko.flattenComputed(config.pages, []);
        this.pageIdx = ko.flattenComputed(config.pageIdx);

        this.areOtherPagesShown = ko.flattenComputed(config.showOtherPages);
        this.spaceBetweenPages = ko.flattenComputed(config.spaceBetweenPages);
        this.otherPageElementWidth = ko.flattenComputed(config.otherPageElementWidth);

        this.effects.register(this.nextShift.subscribe(nextShift => this.update()));
    }

    private getShift() {
        return Math.ceil(this.getShiftRaw());
    }

    private getShiftRaw() {
        const currentPageIdx = this.pageIdx();

        switch (this.alignment()) {
            case WCCSliderAlignment.right:
                return this.pages().reduce((result, page, pageIdx) => {
                    if (pageIdx <= currentPageIdx)
                        return result;
                    else
                        return result - this.getPageWidth(page, pageIdx);
                }, 0);

            case WCCSliderAlignment.left:
                return this.pages().reduce((result, page, pageIdx) => {
                    if (pageIdx < currentPageIdx)
                        return result + this.getPageWidth(page, pageIdx);
                    else if (pageIdx === currentPageIdx)
                        return result - page.borrowedWidth();
                    else
                        return result;
                }, 0);
        }
    }

    private getPageWidth(page: WCCSliderPage<T>, pageIdx: number) {
        return page.width() +
            (!this.areOtherPagesShown() ? this.spaceBetweenPages() : 0) +
            (this.areOtherPagesShown() && pageIdx === 0 ? -this.otherPageElementWidth() : 0);
    }
}